import type { MessageContext } from '@dramadetector/shared';

/**
 * Common communication patterns identified in drama analysis
 * Maps to evidence-based psychological frameworks (attachment theory, CBT, DBT)
 */
export enum PatternType {
  // Attachment Patterns
  ANXIOUS_ATTACHMENT = 'anxious_attachment',
  AVOIDANT_ATTACHMENT = 'avoidant_attachment',
  ANXIOUS_AVOIDANT_CYCLE = 'anxious_avoidant_cycle',
  SECURE_ATTACHMENT = 'secure_attachment',

  // Boundary Patterns
  BOUNDARY_VIOLATION = 'boundary_violation',
  BOUNDARY_EROSION = 'boundary_erosion',
  HEALTHY_BOUNDARY_SETTING = 'healthy_boundary_setting',
  CONFLICT_AVOIDANCE = 'conflict_avoidance',

  // Communication Patterns
  PASSIVE_AGGRESSIVE = 'passive_aggressive',
  STONEWALLING = 'stonewalling',
  DEFENSIVENESS = 'defensiveness',
  CRITICISM = 'criticism',
  LOVE_BOMBING = 'love_bombing',

  // Emotional Patterns
  EMOTIONAL_ESCALATION = 'emotional_escalation',
  EMOTIONAL_WITHDRAWAL = 'emotional_withdrawal',
  TRIANGULATION = 'triangulation',
  GASLIGHTING_POTENTIAL = 'gaslighting_potential',

  // Relationship Dynamics
  POWER_IMBALANCE = 'power_imbalance',
  CODEPENDENCY = 'codependency',
  ENMESHMENT = 'enmeshment',
  HEALTHY_INTERDEPENDENCE = 'healthy_interdependence',
}

/**
 * Context-specific pattern manifestations
 * Same pattern (e.g., boundary violation) manifests differently in work vs dating
 */
export interface PatternContext {
  context: MessageContext;
  manifestation: string; // How pattern appears in this context
  typicalTrigger: string; // What usually causes this in this context
  exampleBehavior: string; // Concrete example
}

/**
 * Pattern Recognition Template
 * Used to generate "PATTERN IDENTIFIED" analysis sections
 */
export interface PatternTemplate {
  patternType: PatternType;
  name: string; // User-friendly pattern name
  description: string; // Plain-language explanation
  realityTVMetaphor: string; // Episode/character reference
  severity: 'low' | 'medium' | 'high' | 'critical';
  contextVariations: PatternContext[];
}

/**
 * Framework-Based Advice Structure
 * Provides actionable guidance without clinical language
 */
export interface AdviceTemplate {
  patternType: PatternType;
  framework: 'attachment_theory' | 'cbt' | 'dbt' | 'boundaries' | 'communication';
  recommendations: {
    immediate: string[]; // What to do right now
    shortTerm: string[]; // Next 1-2 weeks
    longTerm: string[]; // Ongoing practice
  };
  realityCheck: string; // Reality TV-style truth bomb
  contextSpecific: {
    [key in MessageContext]?: string[]; // Context-aware tips
  };
}

/**
 * Reality TV Metaphor Library
 * Bridges clinical insight with pop culture literacy
 */
export interface RealityTVMetaphor {
  id: string;
  show: string; // e.g., "Love Island", "The Bachelor", "Love is Blind"
  metaphor: string; // e.g., "Episode 3 energy", "Couples up too fast"
  psychologicalConcept: string; // e.g., "Anxious attachment", "Emotional escalation"
  usage: string; // When to use this metaphor
  example: string; // How it sounds in practice
}
